import React, { useState, useRef, useEffect } from "react";
import { api, WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "../lib/api";

const SESSION_KEY = "ayame_chat_session";

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Ayame, your AI concierge. I can help you book an appointment in just a few messages — or you can reach us on WhatsApp anytime. What would you like today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const sessionId = useRef(localStorage.getItem(SESSION_KEY) || crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(SESSION_KEY, sessionId.current);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const r = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: r.data.reply, booking_proposal: r.data.booking_proposal || null },
      ]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `I'm having trouble connecting. Please WhatsApp us at ${WHATSAPP_DISPLAY}.` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const confirmBooking = async (proposal) => {
    if (!proposal || confirming) return;
    setConfirming(true);
    try {
      await api.post("/chat/book", proposal);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            `Done — your booking request has been sent ✨\n\n${proposal.service} on ${proposal.date} at ${proposal.time}.\n\nOur reception will confirm it with you on WhatsApp shortly. Anything else I can help with?`,
        },
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not submit. Please try WhatsApp instead.";
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Sorry — ${detail}` },
      ]);
    } finally {
      setConfirming(false);
    }
  };

  const whatsappFor = (p) => {
    const msg = p
      ? `Hi Ayame Salon! I'd like to book ${p.service} on ${p.date} at ${p.time}. Name: ${p.name}. Phone: ${p.phone}.`
      : "Hi Ayame Salon! I'd like to make a booking.";
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 group"
        data-testid="floating-chatbot-btn"
        aria-label="Open AI assistant"
      >
        <div className="bg-[var(--primary)] text-[var(--accent)] w-14 h-14 rounded-full flex items-center justify-center shadow-luxe group-hover:scale-110 transition-all duration-300 border border-[var(--accent)]">
          <i className={`fa-solid ${open ? "fa-xmark" : "fa-comments"} text-xl`}></i>
        </div>
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] h-[560px] bg-white border border-[var(--border)] shadow-luxe flex flex-col"
          data-testid="chatbot-panel"
        >
          <div className="bg-[var(--primary)] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--accent)] flex items-center justify-center text-white">
              <i className="fa-solid fa-sparkles"></i>
            </div>
            <div className="flex-1">
              <p className="font-display text-lg leading-tight">Ayame</p>
              <p className="text-[10px] uppercase tracking-widest text-white/60">Beauty concierge · Booking assistant</p>
            </div>
            <a
              href={whatsappFor()}
              target="_blank"
              rel="noreferrer"
              title="Switch to WhatsApp"
              className="text-white/80 hover:text-[var(--accent)] text-xs"
              data-testid="chatbot-whatsapp-link"
            >
              <i className="fa-brands fa-whatsapp text-lg"></i>
            </a>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--bg)]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[var(--primary)] text-white rounded-2xl rounded-br-sm"
                      : "bg-white border border-[var(--border)] text-[var(--text)] rounded-2xl rounded-bl-sm"
                  }`}
                  data-testid={`chat-msg-${i}`}
                >
                  {m.content}
                  {m.booking_proposal && (
                    <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid={`chat-booking-card-${i}`}>
                      <p className="text-[10px] uppercase tracking-widest text-[var(--muted)] mb-2">Booking summary</p>
                      <ul className="text-xs space-y-1 mb-3">
                        <li><b>Service:</b> {m.booking_proposal.service}</li>
                        <li><b>Date:</b> {m.booking_proposal.date} at {m.booking_proposal.time}</li>
                        <li><b>Name:</b> {m.booking_proposal.name}</li>
                        <li><b>Phone:</b> {m.booking_proposal.phone}</li>
                      </ul>
                      <button
                        onClick={() => confirmBooking(m.booking_proposal)}
                        disabled={confirming}
                        className="w-full bg-[var(--accent)] text-white text-xs uppercase tracking-widest py-2 rounded-full hover:opacity-90 disabled:opacity-50 mb-2"
                        data-testid={`chat-confirm-booking-${i}`}
                      >
                        {confirming ? "Sending..." : "Confirm & Send to Reception"}
                      </button>
                      <a
                        href={whatsappFor(m.booking_proposal)}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-center w-full border border-[var(--border)] text-xs uppercase tracking-widest py-2 rounded-full hover:bg-[var(--bg)]"
                        data-testid={`chat-whatsapp-booking-${i}`}
                      >
                        <i className="fa-brands fa-whatsapp mr-1"></i> Or send via WhatsApp instead
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[var(--border)] px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm text-[var(--muted)]">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-bounce mx-0.5" />
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-bounce mx-0.5"
                    style={{ animationDelay: "0.15s" }}
                  />
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-bounce mx-0.5"
                    style={{ animationDelay: "0.3s" }}
                  />
                </div>
              </div>
            )}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[var(--border)] bg-white flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Tell Ayame what you'd like to book…"
              className="flex-1 px-4 py-2.5 bg-[var(--bg)] border border-[var(--border)] focus:outline-none focus:border-[var(--accent)] text-sm rounded-full"
              data-testid="chatbot-input"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-[var(--accent)] text-white w-10 h-10 rounded-full disabled:opacity-50"
              data-testid="chatbot-send"
            >
              <i className="fa-solid fa-paper-plane text-sm"></i>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
